import React, { useState, useEffect, useRef } from 'react';
import { Input } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import './ChatWidget.css';
import MessagePippip from './Item/MessagePippipWidget.jsx';
import MessageUser from './Item/MessageUserWidget.jsx';
import { MicIcon, SendIcon } from '../../Component/Icons';
import ChatBubble from './Item/ChatBubble.jsx';

const { TextArea } = Input;

export default function ChatWidget({ groupId: propGroupId }) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [groupId, setGroupId] = useState(propGroupId || null);
    const [step, setStep] = useState(1);
    const [initDone, setInitDone] = useState(false);
    const [open, setOpen] = useState(false);
    const messagesEndRef = useRef(null);

    // vi time format
    const formatVNTime = (isoString) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        // Chuyển sang UTC+7
        const vietnamOffset = 7 * 60; // phút
        const localOffset = date.getTimezoneOffset();
        const vietnamTime = new Date(date.getTime() + (vietnamOffset + localOffset) * 60000);
        return vietnamTime.toLocaleString('vi-VN', {
            hour: '2-digit',
            minute: '2-digit',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // # gettextfromstep
    const handleGetTextFromStep = async (currentStep, currentGroupId) => {
        try {
            const nextStepRes = await fetch(
                `${process.env.REACT_APP_API_SERVER_URL}/chat/gettextfromstep?step=${currentStep}`,
                {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' }
                }
            );
            const nextStepData = await nextStepRes.json();

            // send gettextfromstep (step >= 7)
            if (currentStep >= 7 && currentGroupId) {
                await fetch(`${process.env.REACT_APP_API_SERVER_URL}/chat/sendmessage`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        message: nextStepData.message,
                        step: currentStep,
                        sender: 0,
                        groupid: currentGroupId
                    })
                });
            }

            // add message to show
            setMessages(prevMessages => [
                ...prevMessages,
                { sender: '0', message: nextStepData.message, step: currentStep, created_at: new Date().toISOString() }
            ]);

            return nextStepData;
        } catch (error) {
            console.error(`Error in gettextfromstep step ${currentStep}:`, error);
            throw error;
        }
    };

    // # getgroupdetail
    const reloadConversation = async (targetGroupId) => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_SERVER_URL}/chat/getgroupdetail/${targetGroupId}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });
            const data = await res.json();

            const historyMessages = data.list_message.map(m => ({
                sender: m.sender.toString(),
                message: m.message,
                step: m.mess_step,
                created_at: m.created_at
            }));

            setMessages(historyMessages);
            setStep(data.group_step);
            return { data, historyMessages };
        } catch (error) {
            console.error('Error getgroupdetail:', error);
            throw error;
        }
    };

    // # createquotation
    const handleCreateQuotation = async (historyMessages, targetGroupId) => {
        try {
            const userMessages = historyMessages.filter(msg => msg.sender === '1');
            const member = userMessages.find(msg => msg.step === 1)?.message || '';
            const pickup_time = userMessages.find(msg => msg.step === 2)?.message || '';
            const pickup_address = userMessages.find(msg => msg.step === 3)?.message || '';
            const destination = userMessages.find(msg => msg.step === 4)?.message || '';
            const return_time = userMessages.find(msg => msg.step === 5)?.message || '';
            const contact_info = userMessages.find(msg => msg.step === 6)?.message || '';
            const [customer_name, customer_phone] = contact_info ? contact_info.split(' - ') : ['', ''];

            const quotationPayload = {
                member, pickup_time, pickup_address, destination, return_time, customer_name, customer_phone
            };

            // createquotation
            const quotationRes = await fetch(`${process.env.REACT_APP_API_SERVER_URL}/orderquotation/create`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(quotationPayload)
            });
            const quotationData = await quotationRes.json();

            // Gửi link báo giá hoặc error message
            const messageText = quotationData.data?.link
                ? quotationData.data.link
                : 'Xin lỗi, có lỗi xảy ra khi tạo báo giá. Vui lòng thử lại sau.';

            await fetch(`${process.env.REACT_APP_API_SERVER_URL}/chat/sendmessage`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: messageText,
                    step: 7,
                    sender: 0,
                    groupid: targetGroupId
                })
            });

            await reloadConversation(targetGroupId);

        } catch (error) {
            console.error('Error createquotation:', error);

            // Gửi error message
            try {
                await fetch(`${process.env.REACT_APP_API_SERVER_URL}/chat/sendmessage`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        message: 'Xin lỗi, có lỗi xảy ra khi tạo báo giá. Vui lòng thử lại sau.',
                        step: 7,
                        sender: 0,
                        groupid: targetGroupId
                    })
                });
                await reloadConversation(targetGroupId);
            } catch (sendError) {
                console.error('Error sending error message:', sendError);
            }
        }
    };

    // Khởi tạo hoặc load hội thoại
    useEffect(() => {
        if (!open || initDone) return;

        const initializeChat = async () => {
            if (!groupId) {
                // NEW CONVERSATION
                try {
                    await handleGetTextFromStep(1, null);
                    setStep(1);
                    setInitDone(true);
                } catch (error) {
                    console.error('Error initializing new chat:', error);
                }
            } else {
                // EXISTING CONVERSATION
                try {
                    const { data } = await reloadConversation(groupId);
                    setGroupId(data.groupid);

                    if (data.group_step < 7) {
                        await handleGetTextFromStep(data.group_step, data.groupid);
                    }
                    setInitDone(true);
                } catch (error) {
                    console.error('Error loading conversation:', error);
                }
            }
        };

        initializeChat();
    }, [groupId, initDone, open]);

    // Tự động scroll xuống cuối khi messages thay đổi
    useEffect(() => {
        if (open && messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, open]);

    // # sendmessage (userMessage only)
    const handleSend = async () => {
        if (!input.trim()) return;

        const userMessage = input.trim();
        setInput('');

        try {
            const payload = {
                message: userMessage,
                step: step >= 7 ? step + 1 : step,
                sender: 1,
                groupid: groupId || null
            };

            const res = await fetch(`${process.env.REACT_APP_API_SERVER_URL}/chat/sendmessage`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();

            setGroupId(data.groupid);

            // getgroupdetail
            const { data: detailData, historyMessages } = await reloadConversation(data.groupid);

            // gettextfromstep
            if (detailData.group_step <= 7) {
                await handleGetTextFromStep(detailData.group_step, data.groupid);

                // createquotation
                if (detailData.group_step === 7) {
                    setTimeout(() => {
                        handleCreateQuotation(historyMessages, data.groupid);
                    }, 1000);
                }
            }
        } catch (error) {
            console.error('Error sending message:', error);
            setInput(userMessage);
        }
    };

    const handleNewChat = () => {
        setInitDone(false);
        setGroupId(null);
        setMessages([]);
        setStep(1);
    };

    return (
        <>
            {!open && (
                <ChatBubble onClick={() => setOpen(true)} />
            )}
            {open && (
                <div className="chat-widget-container">
                    <div className="chat-widget-header">
                        <div>
                            <div className="chat-widget-header-label-container">
                                <span className="chat-widget-header-label"> Tư vấn đặt xe</span>
                                <span
                                    className="chat-widget-header-new-chat"
                                    onClick={handleNewChat}
                                    role="button"
                                    tabIndex={0}
                                >
                                    Đoạn chat mới
                                </span>
                            </div>
                            {groupId
                                ? <a className="chat-widget-header-group-id"
                                    href={`/chat/${groupId}`}
                                    target="_blank"
                                    rel="noopener noreferrer">{groupId}
                                </a>
                                : null
                            }
                        </div>
                        <button onClick={() => {
                            setOpen(false);
                        }} style={{ background: 'transparent', display: 'flex', border: 'none', boxShadow: 'none' }}>
                            <CloseOutlined style={{ fontSize: 16, display: 'block' }} />
                        </button>
                    </div>
                    <div className="chat-widget-messages">
                        {messages.map((msg, idx) =>
                            msg.sender === '0' ? (
                                <MessagePippip key={idx} message={msg.message} time={formatVNTime(msg.created_at)} />
                            ) : (
                                <MessageUser key={idx} message={msg.message} time={formatVNTime(msg.created_at)} />
                            )
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                    <div className="chat-widget-input-row">
                        <div className="chat-widget-input-group">
                            <TextArea
                                className="chat-widget-input"
                                placeholder="Nhập tin nhắn..."
                                value={input}
                                onChange={e => setInput(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSend();
                                    }
                                }}
                                style={{ resize: 'none' }}
                                autoSize={{ minRows: 1, maxRows: 5 }}
                            />
                            <button className="chat-widget-mic-btn" tabIndex={-1}>
                                <MicIcon />
                            </button>
                            <button className="chat-widget-send-btn" onClick={handleSend}>
                                <SendIcon />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
